import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { fmtTime } from "@/lib/format";
import { CalendarDays, Plus, X, MapPin, Check, Trash2, Clock, Send } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["inspection", "estimate", "install", "service", "follow-up"];
const TYPE_TONE = { inspection: "navy", estimate: "amber", install: "red", service: "blue", "follow-up": "slate" };

function dayKey(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }); }
  catch { return "Unscheduled"; }
}

function NewApptModal({ contacts, onClose, onSaved }) {
  const [f, setF] = useState({ contact_id: "", type: "inspection", scheduled_at: "", address: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.contact_id || !f.scheduled_at) return toast.error("Pick a customer and time");
    setSaving(true);
    try {
      const c = contacts.find((x) => x.id === f.contact_id);
      await api.post("/appointments", { ...f, scheduled_at: new Date(f.scheduled_at).toISOString(), address: f.address || c?.address || "" });
      toast.success("Appointment scheduled"); onSaved();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="mb-card p-6 w-full max-w-md animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="new-appt-modal">
        <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl font-bold uppercase text-navy">New Appointment</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        <label className="text-xs font-semibold uppercase text-slate-500">Customer</label>
        <select data-testid="appt-contact" value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} className="mt-1 mb-3 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
          <option value="">Select…</option>{contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="text-xs font-semibold uppercase text-slate-500">Type</label>
        <select data-testid="appt-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="mt-1 mb-3 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <label className="text-xs font-semibold uppercase text-slate-500">Date & Time</label>
        <input data-testid="appt-time" type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className="mt-1 mb-4 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
        <Btn variant="red" onClick={save} disabled={saving} className="w-full" testid="save-appt"><Plus className="w-4 h-4" /> Schedule</Btn>
      </div>
    </div>
  );
}

export default function Scheduling() {
  const [appts, setAppts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [modal, setModal] = useState(false);
  const load = () => api.get("/appointments").then((r) => setAppts(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  const act = async (a, patch) => { try { await api.put(`/appointments/${a.id}`, patch); load(); } catch { toast.error("Failed"); } };
  const del = async (a) => { try { await api.delete(`/appointments/${a.id}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); } };
  const remind = async (a) => {
    try { const { data } = await api.post(`/appointments/${a.id}/remind`); toast.success(data.mocked ? "Reminder queued (connect Twilio to text it)" : "Reminder texted to customer"); }
    catch { toast.error("Failed to send reminder"); }
  };

  const active = appts.filter((a) => a.status !== "deleted");
  const groups = {};
  active.forEach((a) => { const k = dayKey(a.scheduled_at); (groups[k] = groups[k] || []).push(a); });

  return (
    <div>
      <PageHeader title="Scheduling" subtitle="Book inspections, estimates and installs — reminders auto-send to customers."
        actions={<Btn variant="red" onClick={() => setModal(true)} testid="new-appt-btn"><Plus className="w-4 h-4" /> New Appointment</Btn>} />
      {!active.length && <div className="mb-card p-10 text-center text-slate-400" data-testid="sched-empty"><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" /> No appointments yet — schedule your first one.</div>}
      <div className="space-y-5">
        {Object.entries(groups).map(([day, items]) => (
          <div key={day} data-testid="sched-day-group">
            <div className="flex items-center gap-2 mb-2"><CalendarDays className="w-4 h-4 text-brandred" /><h3 className="font-display font-bold uppercase text-navy text-sm">{day}</h3><span className="text-xs text-slate-400">({items.length})</span></div>
            <div className="space-y-2">
              {items.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).map((a) => (
                <div key={a.id} className="mb-card p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`appt-${a.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-center shrink-0"><Clock className="w-4 h-4 text-slate-400 mx-auto" /><div className="text-xs font-num text-navy font-bold">{fmtTime(a.scheduled_at).split(",").pop().trim()}</div></div>
                    <div className="min-w-0"><div className="font-semibold text-navy truncate">{a.contact?.name || "Customer"}</div>
                      {a.address && <div className="text-xs text-slate-500 flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {a.address}</div>}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pill tone={TYPE_TONE[a.type] || "slate"}>{a.type}</Pill>
                    <Pill tone={a.status === "completed" ? "green" : a.status === "cancelled" ? "danger" : "navy"}>{a.status || "scheduled"}</Pill>
                    {a.status !== "completed" && <button data-testid={`appt-remind-${a.id}`} onClick={() => remind(a)} className="text-navy hover:text-brandred flex items-center gap-1 text-xs font-semibold" title="Text reminder"><Send className="w-3.5 h-3.5" /></button>}
                    {a.status !== "completed" && <button data-testid={`appt-done-${a.id}`} onClick={() => act(a, { status: "completed" })} className="text-successg hover:scale-110 transition" title="Mark done"><Check className="w-4 h-4" /></button>}
                    <button data-testid={`appt-del-${a.id}`} onClick={() => del(a)} className="text-slate-400 hover:text-brandred" title="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {modal && <NewApptModal contacts={contacts} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
    </div>
  );
}
